import html2canvas from 'html2canvas';
import { buildArtboardCss } from './mediaCreatorRenderDocument';
import { documentDesignService } from '../../../services/documentDesignService';
import { blobToDataUrl } from './mediaCreatorExportCss';
import { findLogoImage, isLogoImage } from './mediaCreatorDomRegistry';
import { CUTOUT_ATTR, lookupAssetUrl } from './mediaCreatorHtmlResolve';
import {
  appliedPhotoZoom,
  bakeContainImageToDataUrl,
  bakeContainImageTightToDataUrl,
  bakeCoverImageToDataUrl,
  parseObjectPosition,
  stripZoomTransform,
} from './mediaCreatorPhotoFit';
import { materializeExportDecorations } from './mediaCreatorExportDecor';
import { collectExportDiagnostics } from './mediaCreatorExportDiagnostics';

export { blobToDataUrl, sanitizeCssForExport } from './mediaCreatorExportCss';

const waitForImages = async (root) => {
  const imgs = [...root.querySelectorAll('img[src]')];
  await Promise.all(
    imgs.map(
      (img) =>
        img.complete
          ? Promise.resolve()
          : new Promise((resolve) => {
              img.onload = resolve;
              img.onerror = resolve;
            }),
    ),
  );
  await Promise.all(imgs.map((img) => img.decode?.().catch(() => undefined)));
};

function isCrossOriginSrc(src) {
  if (!src || src.startsWith('blob:') || src.startsWith('data:')) return false;
  try {
    const url = new URL(src, window.location.href);
    return url.origin !== window.location.origin;
  } catch {
    return false;
  }
}

function resolveAssetIdForImg(img, layout) {
  const direct = img.getAttribute('data-media-asset-id');
  if (direct) return direct;

  const slot = img.getAttribute('data-photo-slot');
  if (slot && layout?.photo_bindings?.[slot]) {
    return String(layout.photo_bindings[slot]);
  }
  if (layout?.logo_binding != null && isLogoImage(img)) {
    return String(layout.logo_binding);
  }
  return null;
}

function renditionForImg(img) {
  if (isLogoImage(img)) return img.getAttribute('data-rendition') || 'original';
  return img.getAttribute('data-rendition') || 'web';
}

const EXPORT_PARITY_CSS = `
  .mc-html-stage-inner .mc-invite .mc-gallery .mc-photo {
    position: relative !important;
  }
  .mc-html-stage-inner .mc-invite .mc-copy {
    overflow: visible !important;
  }
  .mc-html-stage-inner .mc-invite .mc-logo-row,
  .mc-html-stage-inner .mc-invite .mc-logo-wrap {
    position: relative !important;
    overflow: visible !important;
  }
`;

function buildExportStyleText(layout) {
  return `${buildArtboardCss(layout)}\n${EXPORT_PARITY_CSS}`;
}

/** Disable live artboard styles that can taint html2canvas (raw document_css on ancestors). */
export function quarantineArtboardStyles(artboardRoot) {
  if (!artboardRoot) return () => {};
  const saved = [];
  artboardRoot.querySelectorAll('style[data-mc-export-quarantine]').forEach((el) => {
    saved.push({ el, text: el.textContent });
    el.textContent = '';
  });
  return () => {
    saved.forEach(({ el, text }) => {
      el.textContent = text;
    });
  };
}

export function exportCaptureRoot(artboardRoot) {
  if (!artboardRoot) return artboardRoot;
  return (
    artboardRoot.querySelector('.mc-html-stage-inner')
    || artboardRoot.querySelector('.mc-html-host')
    || artboardRoot
  );
}

function exportHostElement(artboardRoot) {
  return artboardRoot?.querySelector?.('.mc-html-host') || null;
}

/**
 * Inject export CSS, quarantine live styles, fix capture geometry, hide overlay.
 * @returns {() => void}
 */
export function prepareArtboardForExport(artboardRoot, layout) {
  const captureEl = exportCaptureRoot(artboardRoot) || artboardRoot;
  if (!captureEl) return () => {};

  const restoreQuarantine = quarantineArtboardStyles(artboardRoot);

  const host = exportHostElement(artboardRoot);
  const exportCssText = buildExportStyleText(layout);
  const styleEl = document.createElement('style');
  styleEl.setAttribute('data-mc-export-styles', '1');
  styleEl.textContent = exportCssText;
  (host || captureEl).prepend(styleEl);
  const w = layout?.size_px?.width || 1920;
  const h = layout?.size_px?.height || 1080;
  let hostRestore = null;
  if (host) {
    hostRestore = {
      host,
      transform: host.style.transform,
      width: host.style.width,
      height: host.style.height,
    };
    host.style.transform = 'none';
    host.style.width = `${w}px`;
    host.style.height = `${h}px`;
  }

  let captureRestore = null;
  if (captureEl !== host) {
    captureRestore = {
      captureEl,
      width: captureEl.style.width,
      height: captureEl.style.height,
    };
    captureEl.style.width = `${w}px`;
    captureEl.style.height = `${h}px`;
  }

  const hiddenLayers = [];
  (host || artboardRoot).querySelectorAll('.mc-html-selection-layer').forEach((layer) => {
    hiddenLayers.push({ layer, display: layer.style.display });
    layer.style.display = 'none';
  });

  return () => {
    styleEl.remove();
    restoreQuarantine();
    if (hostRestore) {
      hostRestore.host.style.transform = hostRestore.transform;
      hostRestore.host.style.width = hostRestore.width;
      hostRestore.host.style.height = hostRestore.height;
    }
    if (captureRestore) {
      captureRestore.captureEl.style.width = captureRestore.width;
      captureRestore.captureEl.style.height = captureRestore.height;
    }
    hiddenLayers.forEach(({ layer, display }) => {
      layer.style.display = display;
    });
  };
}

async function fetchAssetBlob(assetId, rendition, isLogo) {
  try {
    return await documentDesignService.fetchCompositionAssetBlob(assetId, rendition);
  } catch (err) {
    if (isLogo && rendition !== 'web') {
      return documentDesignService.fetchCompositionAssetBlob(assetId, 'web');
    }
    throw err;
  }
}

async function inlineSingleImage(img, layout, failures) {
  const src = img.getAttribute('src') || '';
  if (!src || src.startsWith('data:')) return null;

  const assetId = resolveAssetIdForImg(img, layout);
  const isLogo = isLogoImage(img);
  let blob;

  if (assetId) {
    try {
      blob = await fetchAssetBlob(assetId, renditionForImg(img), isLogo);
    } catch {
      failures.push(String(assetId));
      return null;
    }
  } else if (isCrossOriginSrc(src)) {
    const slot = img.getAttribute('data-photo-slot') || 'photo';
    failures.push(`missing asset id (${slot})`);
    return null;
  } else {
    try {
      const resp = await fetch(src, { credentials: 'same-origin' });
      if (!resp.ok) {
        failures.push(src.slice(0, 80));
        return null;
      }
      blob = await resp.blob();
    } catch {
      failures.push(src.slice(0, 80));
      return null;
    }
  }

  const dataUrl = await blobToDataUrl(blob);
  const exportId = img.getAttribute('data-mc-export-id')
    || String(Math.random()).slice(2);
  img.setAttribute('data-mc-export-id', exportId);
  return {
    img,
    exportId,
    dataUrl,
    src: img.getAttribute('src'),
    crossOrigin: img.getAttribute('crossorigin'),
    visibility: img.style.visibility,
  };
}

/**
 * Replace images with data: URLs so html2canvas cannot taint the canvas.
 * @returns {Promise<() => void>}
 */
export async function inlineArtboardImagesForExport(root, { layout } = {}) {
  if (!root) return () => {};

  const restores = [];
  const failures = [];
  const hiddenEmpty = [];

  const imgs = [...root.querySelectorAll('img')];
  for (const img of imgs) {
    if (!img.getAttribute('src')) {
      hiddenEmpty.push({ img, visibility: img.style.visibility });
      img.style.visibility = 'hidden';
    }
  }

  const inlineResults = await Promise.all(
    imgs.map((img) => inlineSingleImage(img, layout, failures)),
  );

  inlineResults.forEach((entry) => {
    if (!entry) return;
    const { img, dataUrl, src, crossOrigin, visibility } = entry;
    restores.push({ img, src, crossOrigin, visibility });
    img.removeAttribute('crossorigin');
    img.style.visibility = '';
    img.src = dataUrl;
  });

  if (failures.length) {
    hiddenEmpty.forEach(({ img, visibility }) => {
      img.style.visibility = visibility;
    });
    throw new Error(
      `Could not load image(s) for export (${failures.join(', ')}) — sign in again or re-pick the photo.`,
    );
  }

  await waitForImages(root);

  return () => {
    restores.forEach(({ img, src, crossOrigin, visibility }) => {
      if (src != null) img.setAttribute('src', src);
      if (crossOrigin != null) img.setAttribute('crossorigin', crossOrigin);
      else img.removeAttribute('crossorigin');
      if (visibility != null) img.style.visibility = visibility;
      img.removeAttribute('data-mc-export-id');
    });
    hiddenEmpty.forEach(({ img, visibility }) => {
      img.style.visibility = visibility;
    });
  };
}

function measurePhotoSlotBox(img) {
  const w = img.offsetWidth || img.clientWidth;
  const h = img.offsetHeight || img.clientHeight;
  if (w > 0 && h > 0) return { w, h };
  const figure = img.closest?.('.mc-photo') || img.parentElement;
  if (figure) {
    const fw = figure.offsetWidth || figure.clientWidth;
    const fh = figure.offsetHeight || figure.clientHeight;
    if (fw > 0 && fh > 0) return { w: fw, h: fh };
  }
  return { w, h };
}

/**
 * html2canvas ignores object-fit:cover — bake each slot to a pre-cropped bitmap.
 * @returns {() => void}
 */
export async function bakePhotoSlotsForExport(root, { layout, assetUrls, exportScale = 1 } = {}) {
  if (!root) return () => {};

  const restores = [];
  void root.offsetHeight;
  const imgs = [...root.querySelectorAll('img[data-photo-slot][src]')];

  for (const img of imgs) {
    await img.decode?.().catch(() => undefined);
    const slot = img.getAttribute('data-photo-slot');
    const objectPosition =
      img.style.objectPosition
      || layout?.photo_object_positions?.[slot]
      || '50% 50%';

    let { w, h } = measurePhotoSlotBox(img);
    if (!(w > 0 && h > 0)) continue;

    w = Math.max(1, Math.round(w * exportScale));
    h = Math.max(1, Math.round(h * exportScale));

    const zoom = appliedPhotoZoom(img);
    let baked;
    if (img.hasAttribute(CUTOUT_ATTR)) {
      const slotAssetId = layout?.photo_bindings?.[slot];
      const info = slotAssetId != null ? lookupAssetUrl(assetUrls, slotAssetId) : null;
      baked = bakeContainImageToDataUrl(img, w, h, objectPosition, {
        isVector: info?.is_vector,
        mimeType: info?.mime_type,
        widthPx: info?.width_px,
        heightPx: info?.height_px,
      }, zoom);
    } else {
      baked = bakeCoverImageToDataUrl(img, w, h, objectPosition, zoom);
    }
    if (!baked) continue;

    restores.push({
      img,
      src: img.src,
      objectFit: img.style.objectFit,
      objectPosition: img.style.objectPosition,
      width: img.style.width,
      height: img.style.height,
      transform: img.style.transform,
      clipPath: img.style.clipPath,
    });

    img.src = baked;
    img.style.objectFit = 'fill';
    img.style.objectPosition = '50% 50%';
    img.style.width = `${Math.round(w / exportScale)}px`;
    img.style.height = `${Math.round(h / exportScale)}px`;
    if (zoom !== 1) {
      img.style.transform = stripZoomTransform(img.style.transform);
      img.style.removeProperty('clip-path');
    }
  }

  await waitForImages(root);

  return () => {
    restores.forEach(({ img, src, objectFit, objectPosition, width, height, transform, clipPath }) => {
      img.src = src;
      img.style.objectFit = objectFit;
      img.style.objectPosition = objectPosition;
      img.style.width = width;
      img.style.height = height;
      img.style.transform = transform;
      img.style.clipPath = clipPath;
    });
  };
}

function logoPositionShell(img) {
  return img.closest?.('.mc-logo-row, .mc-logo-wrap') || img.parentElement;
}

/**
 * html2canvas ignores object-fit:contain and SVG <img> sizing — bake Brand Library vectors to sharp bitmaps.
 * @returns {{ restore: () => void, logoExportMeta: object | null }}
 */
export async function bakeLogoForExport(root, { exportScale = 1, layout, assetUrls } = {}) {
  const empty = { restore: () => {}, logoExportMeta: null };
  if (!root) return empty;

  const img = findLogoImage(root);
  if (!img || !img.getAttribute('src')) return empty;

  const restores = [];
  void root.offsetHeight;

  await img.decode?.().catch(() => undefined);

  const row = logoPositionShell(img);
  const savedTransform = img.style.transform;
  const savedTransformOrigin = img.style.transformOrigin;
  const savedPosition = img.style.position;
  const savedLeft = img.style.left;
  const savedTop = img.style.top;
  const savedRowPosition = row?.style.position;

  const visualRect = img.getBoundingClientRect();
  const rowRect = row?.getBoundingClientRect() || visualRect;
  const visualW = Math.max(1, visualRect.width);
  const visualH = Math.max(1, visualRect.height);
  const offsetLeft = visualRect.left - rowRect.left;
  const offsetTop = visualRect.top - rowRect.top;

  img.style.transform = 'none';
  void img.offsetHeight;

  const logoTransformBeforeBake = savedTransform || getComputedStyle(img).transform || 'none';

  if (!(visualW > 0 && visualH > 0)) {
    img.style.transform = savedTransform;
    return empty;
  }

  const objectPosition =
    img.style.objectPosition || getComputedStyle(img).objectPosition || 'left center';
  const bakeW = Math.max(1, Math.round(visualW * exportScale));
  const bakeH = Math.max(1, Math.round(visualH * exportScale));

  const assetId = resolveAssetIdForImg(img, layout);
  const assetInfo = assetId && assetUrls ? lookupAssetUrl(assetUrls, assetId) : null;
  const rasterHints = {
    isVector: assetInfo?.is_vector,
    mimeType: assetInfo?.mime_type,
    widthPx: assetInfo?.width_px,
    heightPx: assetInfo?.height_px,
  };
  const baked = bakeContainImageTightToDataUrl(img, bakeW, bakeH, objectPosition, rasterHints);
  if (!baked?.dataUrl) {
    img.style.transform = savedTransform;
    return empty;
  }

  if (!img.getAttribute('data-mc-export-id')) {
    img.setAttribute('data-mc-export-id', String(Math.random()).slice(2));
  }

  restores.push({
    img,
    row,
    src: img.src,
    objectFit: img.style.objectFit,
    objectPosition: img.style.objectPosition,
    width: img.style.width,
    height: img.style.height,
    transform: savedTransform,
    transformOrigin: savedTransformOrigin,
    position: savedPosition,
    left: savedLeft,
    top: savedTop,
    rowPosition: savedRowPosition,
  });

  if (row) {
    row.style.position = 'relative';
  }
  const drawnW = baked.width / exportScale;
  const drawnH = baked.height / exportScale;
  const pos = parseObjectPosition(objectPosition);
  const drawnLeft = offsetLeft + (visualW - drawnW) * (pos.x / 100);
  const drawnTop = offsetTop + (visualH - drawnH) * (pos.y / 100);

  img.src = baked.dataUrl;
  img.style.objectFit = 'fill';
  img.style.objectPosition = '50% 50%';
  img.style.position = 'absolute';
  img.style.left = `${drawnLeft}px`;
  img.style.top = `${drawnTop}px`;
  img.style.width = `${drawnW}px`;
  img.style.height = `${drawnH}px`;
  img.style.maxWidth = 'none';
  img.style.maxHeight = 'none';
  img.style.transform = 'none';
  img.style.transformOrigin = 'top left';

  const logoExportMeta = {
    logoTransform: logoTransformBeforeBake,
    logoVisualBox: {
      width: Math.round(visualW),
      height: Math.round(visualH),
      left: Math.round(offsetLeft),
      top: Math.round(offsetTop),
    },
    logoBakeSize: { width: baked.width, height: baked.height },
  };

  await waitForImages(root);

  return {
    logoExportMeta,
    restore: () => {
    restores.forEach((entry) => {
      const logo = entry.img;
      logo.src = entry.src;
      logo.style.objectFit = entry.objectFit;
      logo.style.objectPosition = entry.objectPosition;
      logo.style.width = entry.width;
      logo.style.height = entry.height;
      logo.style.transform = entry.transform;
      logo.style.transformOrigin = entry.transformOrigin;
      logo.style.position = entry.position;
      logo.style.left = entry.left;
      logo.style.top = entry.top;
      logo.style.maxWidth = '';
      logo.style.maxHeight = '';
      if (entry.row) entry.row.style.position = entry.rowPosition;
    });
    },
  };
}

function syncImgStyles(liveImg, cloneImg) {
  if (!cloneImg || !liveImg.src) return;
  cloneImg.src = liveImg.src;
  cloneImg.removeAttribute('crossorigin');
  cloneImg.style.objectFit = liveImg.style.objectFit;
  cloneImg.style.objectPosition = liveImg.style.objectPosition;
  cloneImg.style.width = liveImg.style.width;
  cloneImg.style.height = liveImg.style.height;
  cloneImg.style.transform = liveImg.style.transform;
  cloneImg.style.transformOrigin = liveImg.style.transformOrigin;
  cloneImg.style.visibility = liveImg.style.visibility;
}

function syncCloneImages(liveRoot, clonedRoot) {
  const liveImgs = [...liveRoot.querySelectorAll('img[data-mc-export-id]')];
  const cloneImgs = [...clonedRoot.querySelectorAll('img')];
  liveImgs.forEach((liveImg) => {
    const id = liveImg.getAttribute('data-mc-export-id');
    let cloneImg = clonedRoot.querySelector(`img[data-mc-export-id="${id}"]`);
    if (!cloneImg && cloneImgs.length === liveImgs.length) {
      const idx = liveImgs.indexOf(liveImg);
      cloneImg = cloneImgs[idx];
    }
    syncImgStyles(liveImg, cloneImg);
  });

  const liveLogo = findLogoImage(liveRoot);
  const cloneLogo = findLogoImage(clonedRoot);
  if (liveLogo && cloneLogo && liveLogo !== cloneLogo) {
    syncImgStyles(liveLogo, cloneLogo);
  }
}

const MC_EXPORT_FONT_STYLESHEET =
  'https://fonts.googleapis.com/css2?family=Montserrat:wght@600;700;800&family=Source+Sans+3:wght@400;600;700&display=swap';

function scrubClonedDocument(clonedDoc, exportCssText, liveCaptureEl) {
  clonedDoc.querySelectorAll('link[rel="stylesheet"]').forEach((link) => link.remove());
  clonedDoc.querySelectorAll('style').forEach((style) => style.remove());
  const head = clonedDoc.head || clonedDoc.documentElement;
  const fontLink = clonedDoc.createElement('link');
  fontLink.rel = 'stylesheet';
  fontLink.href = MC_EXPORT_FONT_STYLESHEET;
  head.appendChild(fontLink);
  const styleEl = clonedDoc.createElement('style');
  styleEl.setAttribute('data-mc-export-styles', '1');
  styleEl.textContent = exportCssText;
  head.appendChild(styleEl);

  const clonedRoot =
    clonedDoc.querySelector('.mc-html-stage-inner')
    || clonedDoc.querySelector('.mc-html-host')
    || clonedDoc.body;
  if (clonedRoot && liveCaptureEl) {
    syncCloneImages(liveCaptureEl, clonedRoot);
    clonedRoot.querySelectorAll('.mc-html-selection-layer').forEach((layer) => {
      layer.style.display = 'none';
    });
  }
}

export async function captureArtboardCanvas(
  root,
  { scale = 2, backgroundColor = null, layout, assetUrls } = {},
) {
  const captureEl = exportCaptureRoot(root) || root;
  const exportCssText = buildExportStyleText(layout);
  const w = layout?.size_px?.width || 1920;
  const h = layout?.size_px?.height || 1080;

  const restorePrepare = prepareArtboardForExport(root, layout);
  const restoreInline = await inlineArtboardImagesForExport(captureEl, { layout });
  const restoreBakePhotos = await bakePhotoSlotsForExport(captureEl, {
    layout,
    assetUrls,
    exportScale: scale,
  });
  const { restore: restoreBakeLogo, logoExportMeta } = await bakeLogoForExport(captureEl, {
    exportScale: scale,
    layout,
    assetUrls,
  });
  const restoreDecor = materializeExportDecorations(captureEl);

  const restore = () => {
    restoreDecor();
    restoreBakeLogo();
    restoreBakePhotos();
    restoreInline();
    restorePrepare();
  };

  try {
    if (typeof document !== 'undefined' && document.fonts?.ready) {
      await document.fonts.ready;
    }
    const canvas = await html2canvas(captureEl, {
      scale,
      width: w,
      height: h,
      useCORS: false,
      allowTaint: false,
      backgroundColor,
      logging: false,
      onclone: (clonedDoc) => {
        scrubClonedDocument(clonedDoc, exportCssText, captureEl);
      },
    });
    let diagnostics = collectExportDiagnostics(captureEl, layout, {
      scale,
      exportMode: 'canvas',
      layoutSource: 'viewer',
      logoExport: logoExportMeta,
    });
    try {
      canvas.toDataURL('image/png');
    } catch (taintErr) {
      diagnostics = collectExportDiagnostics(captureEl, layout, {
        scale,
        exportMode: 'canvas_taint',
        layoutSource: 'viewer',
        logoExport: logoExportMeta,
      });
      const err = new Error(
        taintErr?.message
          || 'Export produced a tainted canvas — save the design and re-pick Brand Library photos if this persists.',
      );
      err.exportDiagnostics = diagnostics;
      throw err;
    }
    return { canvas, restore, diagnostics };
  } catch (err) {
    if (!err.exportDiagnostics) {
      try {
        err.exportDiagnostics = collectExportDiagnostics(captureEl, layout, {
          scale,
          exportMode: 'canvas_error',
          layoutSource: 'viewer',
          logoExport: logoExportMeta,
        });
      } catch {
        /* ignore */
      }
    }
    restore();
    throw err;
  }
}
